import { useMemo, useState } from 'react';
import { Button, Empty, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { NOC_STATION_STATES } from '@goelectric/shared-types';
import type { NocStationState, NocStationSummary } from '@goelectric/shared-types';
import { NOC_STATION_STATE_META } from '@goelectric/ui-components';
import { SemaphoreBadge, StationStateBadge } from './StatusBadge';
import { formatDateTime, formatRelative, isDemoName } from '../utils/format';

interface StationTableProps {
  stations: NocStationSummary[];
  selectedStationId: string | null;
  onSelectStation: (stationId: string) => void;
}

/** Tabla de estaciones del NOC: búsqueda, filtro por estado, orden, paginación y vacío. */
export function StationTable({ stations, selectedStationId, onSelectStation }: StationTableProps) {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<NocStationState | 'ALL'>('ALL');

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return stations.filter((station) => {
      if (stateFilter !== 'ALL' && station.state !== stateFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [station.stationId, station.name, station.city].some((value) =>
        (value ?? '').toLowerCase().includes(term),
      );
    });
  }, [stations, query, stateFilter]);

  const columns: ColumnsType<NocStationSummary> = [
    {
      title: 'Estación',
      key: 'station',
      fixed: 'left',
      width: 240,
      sorter: (a, b) => (a.name ?? a.stationId).localeCompare(b.name ?? b.stationId),
      render: (_, station) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>
            {station.name ?? station.stationId}{' '}
            {isDemoName(station.name) && <Tag color="gold">DEMO</Tag>}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
            {station.stationId}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Ciudad',
      dataIndex: 'city',
      key: 'city',
      width: 140,
      render: (city: string | null) => city ?? '—',
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      width: 170,
      filters: NOC_STATION_STATES.map((state) => ({
        text: NOC_STATION_STATE_META[state].label,
        value: state,
      })),
      onFilter: (value, station) => station.state === value,
      render: (state: NocStationState) => <StationStateBadge state={state} />,
    },
    {
      title: 'Semáforo',
      dataIndex: 'semaphore',
      key: 'semaphore',
      width: 150,
      render: (_, station) => <SemaphoreBadge semaphore={station.semaphore} />,
    },
    {
      title: 'Conectores libres',
      key: 'connectors',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.connectors.byState.AVAILABLE - b.connectors.byState.AVAILABLE,
      render: (_, station) =>
        `${station.connectors.byState.AVAILABLE}/${station.connectors.total}`,
    },
    {
      title: 'Sesiones',
      dataIndex: 'activeSessions',
      key: 'activeSessions',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.activeSessions - b.activeSessions,
      render: (value: number) => (value > 0 ? <strong>{value}</strong> : '0'),
    },
    {
      title: 'Última señal',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 150,
      sorter: (a, b) => (a.lastSeenAt ?? '').localeCompare(b.lastSeenAt ?? ''),
      render: (lastSeenAt: string | null) => (
        <Tooltip title={formatDateTime(lastSeenAt)}>
          <span>{formatRelative(lastSeenAt)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 90,
      render: (_, station) => (
        <Button size="small" onClick={() => onSelectStation(station.stationId)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por ID, nombre o ciudad"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 280 }}
          aria-label="Buscar estaciones"
        />
        <Select
          value={stateFilter}
          onChange={setStateFilter}
          style={{ width: 200 }}
          aria-label="Filtrar por estado"
          options={[
            { value: 'ALL', label: 'Todos los estados' },
            ...NOC_STATION_STATES.map((state) => ({
              value: state,
              label: NOC_STATION_STATE_META[state].label,
            })),
          ]}
        />
        <Typography.Text type="secondary">
          {filtered.length} de {stations.length} estaciones
        </Typography.Text>
      </Space>

      <Table<NocStationSummary>
        rowKey="stationId"
        size="middle"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 8, showSizeChanger: false, showTotal: (total) => `${total} estaciones` }}
        scroll={{ x: 1100 }}
        rowClassName={(station) => (station.stationId === selectedStationId ? 'noc-row--selected' : '')}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin estaciones para el filtro" />,
        }}
      />
    </div>
  );
}
